import {
  BusinessUnitIcon,
  CommunityIcon,
  CorporationIcon,
  KensakuKunIcon,
  PersonalIcon,
  SearchIcon,
} from "components/icons";

import {
  EBusinessUnit,
  ENav,
  ENavTab,
  EPersonal,
  TNavItem,
  TStaticChildNav,
} from "./types";
import { EAccessType } from "types/index";
import {
  BU_ACCESS_RIGHT,
  CORP_ACCESS_RIGHT,
  OPSNAVI_ACCESS_RIGHT,
} from "constants/accessRight";
import { EACOUNT_TYPE } from "constants/index";
import { routePaths } from "routes/constants";

export const DEFAULT_NAV_ITEMS: TNavItem[] = [
  {
    name: ENav.PERSONAL,
    label: "personal_account",
    href: routePaths.root.root,
    icon: <PersonalIcon width={20} height={20} />,
  },
  {
    name: ENav.BUSINESS_UNIT,
    label: "business_unit",
    href: routePaths.root.bu.root.root,
    hrefShowMore: routePaths.root.bu.root.root,
    icon: <BusinessUnitIcon width={20} height={20} />,
  },
  {
    label: "corporation",
    name: ENav.CORPORATION,
    href: routePaths.root.corp.root.root,
    hrefShowMore: routePaths.root.corp.root.root,
    icon: <CorporationIcon width={20} height={20} />,
  },
  {
    label: "community",
    name: ENav.COMMUNITY,
    href: routePaths.root.com.root.root,
    hrefShowMore: routePaths.root.com.root.root,
    icon: <CommunityIcon width={20} height={20} />,
  },
  {
    label: "kensaku_kun",
    name: ENav.KENSAKU_KUN,
    href: routePaths.root.ken.root,
    icon: <KensakuKunIcon width={20} height={20} />,
  },
  {
    superAdminOnly: true,
    label: "search",
    name: ENav.SEARCH,
    href: routePaths.root.search.root,
    icon: <SearchIcon width={20} height={20} />,
  },
];

export const SUPER_ADMIN_PERSONAL_CHILD_NAV: Record<string, TStaticChildNav> = {
  [ENavTab.GENERAL_SETTING]: {
    name: "general_settings",
    slug: routePaths.root.root,
  },
  [ENavTab.STAFF]: {
    name: "staffs",
    slug: routePaths.root.staff.root.root,
  },
  [ENavTab.CLIENT]: {
    name: "clients",
    slug: routePaths.root.client.root.root,
  },
  [ENavTab.EMPLOYEE]: {
    name: "hs_employee",
    slug: routePaths.root.employee.root.root,
  },
};

export const PATHS_TO_HIDE = [...DEFAULT_NAV_ITEMS];

export const SIDEBAR_WIDTH = 56;

export const MAX_LENGTH_NAV_CHILDREN = 5;

export const MINIMUM_MAIN_PATH_LENGTH = {
  [ENav.PERSONAL]: 1,
  [ENav.CORPORATION]: 3,
  [ENav.BUSINESS_UNIT]: 3,
  [ENav.COMMUNITY]: 4,
  [ENav.SEARCH]: 1,
  [ENav.KENSAKU_KUN]: 1,
};

export const TAB_LABEL: Record<ENavTab, string> = {
  [ENavTab.GENERAL_SETTING]: "general_settings",
  [ENavTab.STAFF]: "staffs",
  [ENavTab.EMPLOYEE]: "hs_employee",
  [ENavTab.CLIENT]: "clients",
  [ENavTab.MEMBER]: "members",
  [ENavTab.MY_ROLE]: "my_roles",
  [ENavTab.EMPLOYED_CORPORATION]: "employed_corporation",
  [ENavTab.CORPORATION_DETAIL]: "corporation_details",
  [ENavTab.EMPLOYED_STAFF]: "employed_staff",
  [ENavTab.ACTIVE_VACANCIES]: "active_vacancies",
  [ENavTab.MANAGE_POSTINGS]: "manage_postings",
  [ENavTab.ASSIGNED_ACCOUNTS]: "assigned_accounts",
  [ENavTab.MANAGE_MEMBERS_ASSIGNMENT]: "manage_members_assignment",
  [ENavTab.UNASSIGNED_ACCOUNTS]: "unassigned_accounts",
  [ENavTab.BUSINESS_ESTABLISHMENTS]: "business_establishments",
  [ENavTab.PERSONAL_ACCOUNT]: "personal_account",
  [ENavTab.BUSINESS_UNIT]: "business_unit",
  [ENavTab.CORPORATION]: "corporation",
  [ENavTab.COMMUNITY]: "community",
  [ENavTab.EVALUATE]: "evaluate",
  [ENavTab.SCORECARD_HISTORY]: "scorecard_history",
  [ENavTab.MANAGE_TAGS]: "manage_tags",
  [ENavTab.SEARCH_STAFF]: "search_staff",
  [ENavTab.SEARCH_EMPLOYEE]: "search_employee",
  [ENavTab.PAST_NOTES]: "past_notes",
  [ENavTab.SEARCH_CLIENT]: "search_client",
  [ENavTab.SEARCH_CORPORATION]: "search_corporation",
  [ENavTab.SEARCH_JOB_LISTING]: "search_job_listing",
};

export const TAB_LIST: Record<
  string,
  Record<string, string | Record<string, string>>
> = {
  [ENavTab.GENERAL_SETTING]: {
    [ENav.PERSONAL]: {
      [EPersonal.SUPERADMIN]: routePaths.root.generalSetting.root,
      [EPersonal.EMPLOYEE_OWN]: routePaths.root.generalSetting.root,
      [EPersonal.EMPLOYEE]: routePaths.root.employee.root.pEmployeeSlug.root,
      [EPersonal.STAFF]: routePaths.root.staff.root.pStaffSlug.root,
      [EPersonal.CLIENT]: routePaths.root.client.root.pClientSlug.root,
    },
    [ENav.CORPORATION]: routePaths.root.corp.root.pCorpSlug.generalSetting.root,
    [ENav.BUSINESS_UNIT]: {
      [EPersonal.SUPERADMIN]:
        routePaths.root.bu.root.pBuSlug.generalSetting.root,
      [EPersonal.EMPLOYEE_OWN]:
        routePaths.root.bu.root.pBuSlug.generalSetting.root,
    },
    [ENav.COMMUNITY]: "#",
  },
  [ENavTab.STAFF]: {
    [ENav.PERSONAL]: {
      [EPersonal.SUPERADMIN]: routePaths.root.staff.root.root,
    },
  },
  [ENavTab.EMPLOYEE]: {
    [ENav.PERSONAL]: {
      [EPersonal.SUPERADMIN]: routePaths.root.employee.root.root,
    },
  },
  [ENavTab.CLIENT]: {
    [ENav.PERSONAL]: {
      [EPersonal.SUPERADMIN]: routePaths.root.client.root.root,
    },
  },
  [ENavTab.BUSINESS_ESTABLISHMENTS]: {
    [ENav.CORPORATION]:
      routePaths.root.corp.root.pCorpSlug.businessEstablishment.root,
  },
  [ENavTab.MEMBER]: {
    [ENav.PERSONAL]: {
      [EPersonal.EMPLOYEE_OWN]: routePaths.root.member.root,
      [EPersonal.EMPLOYEE]:
        routePaths.root.employee.root.pEmployeeSlug.member.root,
      [EPersonal.STAFF]: routePaths.root.staff.root.pStaffSlug.member.root,
      [EPersonal.CLIENT]: routePaths.root.client.root.pClientSlug.member.root,
    },
    [ENav.CORPORATION]: routePaths.root.corp.root.pCorpSlug.member.root,
    [ENav.BUSINESS_UNIT]: routePaths.root.bu.root.pBuSlug.member.root,
    [ENav.COMMUNITY]: "#",
  },
  [ENavTab.ACTIVE_VACANCIES]: {
    [ENav.CORPORATION]:
      routePaths.root.corp.root.pCorpSlug.activeVacancies.root,
  },
  [ENavTab.MANAGE_POSTINGS]: {
    [ENav.BUSINESS_UNIT]: routePaths.root.bu.root.pBuSlug.managePostings.root,
  },
  [ENavTab.EMPLOYED_CORPORATION]: {
    [ENav.PERSONAL]: {
      [EPersonal.STAFF]:
        routePaths.root.staff.root.pStaffSlug.employedCorporation.root,
    },
  },
  [ENavTab.CORPORATION_DETAIL]: {
    [ENav.PERSONAL]: {
      [EPersonal.CLIENT]:
        routePaths.root.client.root.pClientSlug.corporationDetail.root,
    },
  },
  [ENavTab.EMPLOYED_STAFF]: {
    [ENav.PERSONAL]: {
      [EPersonal.CLIENT]:
        routePaths.root.client.root.pClientSlug.employedStaff.root,
    },
  },
  [ENavTab.MY_ROLE]: {
    [ENav.PERSONAL]: {
      [EPersonal.EMPLOYEE_OWN]: routePaths.root.myRole.root,
    },
  },
  [ENavTab.ASSIGNED_ACCOUNTS]: {
    [ENav.BUSINESS_UNIT]: routePaths.root.bu.root.pBuSlug.assignedAccount.root,
  },
  [ENavTab.MANAGE_MEMBERS_ASSIGNMENT]: {
    [ENav.BUSINESS_UNIT]: {
      [EBusinessUnit.ASSIGNMENT]:
        routePaths.root.bu.root.pBuSlug.assignedAccount.manage
          .manageMemberAssignment.root,
    },
  },
  [ENavTab.UNASSIGNED_ACCOUNTS]: {
    [ENav.BUSINESS_UNIT]: {
      [EBusinessUnit.ASSIGNMENT]:
        routePaths.root.bu.root.pBuSlug.assignedAccount.manage.unassignedAccount
          .root,
    },
  },

  [ENavTab.PERSONAL_ACCOUNT]: {
    [ENav.SEARCH]: routePaths.root.search.personal.root,
  },
  [ENavTab.BUSINESS_UNIT]: {
    [ENav.SEARCH]: routePaths.root.search.bu.root,
  },
  [ENavTab.CORPORATION]: {
    [ENav.SEARCH]: routePaths.root.search.corporation.root,
  },
  [ENavTab.COMMUNITY]: {
    [ENav.SEARCH]: "#",
  },
  [ENavTab.MANAGE_TAGS]: {
    [ENav.BUSINESS_UNIT]: routePaths.root.bu.root.pBuSlug.manageTags.root,
  },

  [ENavTab.EVALUATE]: {
    [ENav.BUSINESS_UNIT]: {
      [ENavTab.SCORECARD_HISTORY]:
        routePaths.root.bu.root.pBuSlug.scorecardHistory.root,
      [ENavTab.PAST_NOTES]:
        routePaths.root.bu.root.pBuSlug.pastNotes.fieldSales.root,
    },
  },

  [ENavTab.PAST_NOTES]: {
    [ENav.PERSONAL]: {
      [EPersonal.STAFF]: routePaths.root.staff.root.pStaffSlug.pastNotes.root,
    },
    [ENav.CORPORATION]: routePaths.root.corp.root.pCorpSlug.pastNotes.root,
  },

  [ENavTab.SEARCH_STAFF]: {
    [ENav.KENSAKU_KUN]: routePaths.root.ken.staff.root,
  },
  [ENavTab.SEARCH_EMPLOYEE]: {
    [ENav.KENSAKU_KUN]: routePaths.root.ken.employee.root,
  },
  [ENavTab.SEARCH_CLIENT]: {
    [ENav.KENSAKU_KUN]: routePaths.root.ken.client.root,
  },
  [ENavTab.SEARCH_CORPORATION]: {
    [ENav.KENSAKU_KUN]: routePaths.root.ken.corporation.root,
  },
  [ENavTab.SEARCH_JOB_LISTING]: {
    [ENav.KENSAKU_KUN]: routePaths.root.ken.jobListing.root,
  },
};

export const TAB_LIST_ACCESSRIGHT: Record<
  string,
  Record<
    string,
    string | number[] | null | Record<string, string | number[] | null>
  >
> = {
  [ENavTab.GENERAL_SETTING]: {
    [ENav.PERSONAL]: {
      [EPersonal.SUPERADMIN]: null,
      [EPersonal.EMPLOYEE_OWN]: null,
      [EPersonal.EMPLOYEE]: null,
      [EPersonal.STAFF]: null,
      [EPersonal.CLIENT]: null,
    },
    [ENav.CORPORATION]: null,
    [ENav.BUSINESS_UNIT]: {
      [EPersonal.SUPERADMIN]: null,
      [EPersonal.EMPLOYEE_OWN]: null,
    },
    [ENav.COMMUNITY]: null,
  },
  [ENavTab.STAFF]: {
    [ENav.PERSONAL]: {
      [EPersonal.SUPERADMIN]: null,
    },
  },
  [ENavTab.EMPLOYEE]: {
    [ENav.PERSONAL]: {
      [EPersonal.SUPERADMIN]: null,
    },
  },
  [ENavTab.CLIENT]: {
    [ENav.PERSONAL]: {
      [EPersonal.SUPERADMIN]: null,
    },
  },
  [ENavTab.BUSINESS_ESTABLISHMENTS]: {
    [ENav.CORPORATION]: [
      CORP_ACCESS_RIGHT.VIEW_COMPANY_INFORMATION,
      CORP_ACCESS_RIGHT.CRUD_COMPANY_INFORMATION,
    ],
  },
  [ENavTab.MEMBER]: {
    [ENav.PERSONAL]: {
      [EPersonal.EMPLOYEE_OWN]: null,
      [EPersonal.EMPLOYEE]: null,
      [EPersonal.STAFF]: null,
      [EPersonal.CLIENT]: null,
    },
    [ENav.CORPORATION]: null,
    [ENav.BUSINESS_UNIT]: null,
    [ENav.COMMUNITY]: null,
  },
  [ENavTab.ACTIVE_VACANCIES]: {
    [ENav.CORPORATION]: [
      CORP_ACCESS_RIGHT.VIEW_COMPANY_INFORMATION,
      CORP_ACCESS_RIGHT.CRUD_COMPANY_INFORMATION,
    ],
  },
  [ENavTab.MANAGE_POSTINGS]: {
    [ENav.BUSINESS_UNIT]: [BU_ACCESS_RIGHT.MANAGE_JOB_DOCUMENT],
  },
  [ENavTab.EMPLOYED_CORPORATION]: {
    [ENav.PERSONAL]: {
      [EPersonal.STAFF]: null,
    },
  },
  [ENavTab.CORPORATION_DETAIL]: {
    [ENav.PERSONAL]: {
      [EPersonal.CLIENT]: null,
    },
  },
  [ENavTab.EMPLOYED_STAFF]: {
    [ENav.PERSONAL]: {
      [EPersonal.CLIENT]: null,
    },
  },
  [ENavTab.MY_ROLE]: {
    [ENav.PERSONAL]: {
      [EPersonal.EMPLOYEE_OWN]: null,
    },
  },
  [ENavTab.ASSIGNED_ACCOUNTS]: {
    [ENav.BUSINESS_UNIT]: null,
  },
  [ENavTab.MANAGE_MEMBERS_ASSIGNMENT]: {
    [ENav.BUSINESS_UNIT]: {
      [EBusinessUnit.ASSIGNMENT]: null,
    },
  },
  [ENavTab.UNASSIGNED_ACCOUNTS]: {
    [ENav.BUSINESS_UNIT]: {
      [EBusinessUnit.ASSIGNMENT]: null,
    },
  },
  [ENavTab.PERSONAL_ACCOUNT]: {
    [ENav.SEARCH]: EACOUNT_TYPE.SUPERADMIN,
  },
  [ENavTab.BUSINESS_UNIT]: {
    [ENav.SEARCH]: EACOUNT_TYPE.SUPERADMIN,
  },
  [ENavTab.CORPORATION]: {
    [ENav.SEARCH]: EACOUNT_TYPE.SUPERADMIN,
  },
  [ENavTab.COMMUNITY]: {
    [ENav.SEARCH]: EACOUNT_TYPE.SUPERADMIN,
  },
  [ENavTab.MANAGE_TAGS]: {
    [ENav.BUSINESS_UNIT]: [OPSNAVI_ACCESS_RIGHT.TAGS_VIEW_TAGS],
  },

  [ENavTab.PAST_NOTES]: {
    [ENav.CORPORATION]: null,
  },

  [ENavTab.EVALUATE]: {
    [ENav.BUSINESS_UNIT]: {
      [ENavTab.SCORECARD_HISTORY]: [
        OPSNAVI_ACCESS_RIGHT.SCORE_CARD_READ_ALL_SCORE_CARDS,
        OPSNAVI_ACCESS_RIGHT.SCORE_CARD_READ_ASSIGNED_SCORE_CARDS,
      ],
      [ENavTab.PAST_NOTES]: null,
    },
  },
};

export const ENAV_TYPE = {
  [EAccessType.CORP]: ENav.CORPORATION,
  [EAccessType.BU]: ENav.BUSINESS_UNIT,
  [EAccessType.COM]: ENav.COMMUNITY,
  [EAccessType.NULL]: ENav.BUSINESS_UNIT,
  [EAccessType.SEARCH]: ENav.SEARCH,
  [EAccessType.KENSAKU_KUN]: ENav.KENSAKU_KUN,
};
